/**
 * @render react
 * @name SocialEmbed
*/

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withAmp } from 'Components/protons/withAmp';
import { withPolaris } from 'Components/protons/Polaris';

import { Video } from 'Components/molecules/Video';

import renderAmp from './amp';

import './_style.scss';

/**
 * SocialEmbed component
 *
 * @param {string} content - Content to be outputted
 * @param {string} type - The type of the social embed
 * @param {string} url - The URL of the social embed
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 */
class SocialEmbed extends PureComponent {
  /**
   * Renders the amp version of the component.
   */
  amp() {
    const {
      url,
    } = this.props;

    // Check if we have a video embed
    if (this.isVideo()) {
      return (
        <Video url={url} />
      );
    }

    return renderAmp.bind(this)();
  }

  /**
   * Checks if we need to render a video as primary media
   */
  isVideo() {
    const {
      type,
      url,
    } = this.props;

    return ((type.toLowerCase() === 'youtube' || type.toLowerCase() === 'vimeo') && url);
  }

  /**
   * Render our component
   */
  render() {
    const {
      children,
      type,
      url,
      loadStyles,
      extraClassNames,
      context: { cssPrefix },
    } = this.props;

    // Check if we have a video embed
    if (this.isVideo()) {
      return (
        <Video url={url} />
      );
    }

    return (
      <div
        className={classNames({
          [`${cssPrefix}__social-embed ${cssPrefix}__social-embed--${type.toLowerCase()}`]: loadStyles,
          ...extraClassNames,
        })}
      >
        {children}
      </div>
    );
  }
}

/**
 * Valid props
 */
SocialEmbed.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string.isRequired,
  url: PropTypes.string,
  loadStyles: PropTypes.bool,
  ampEnable: PropTypes.bool, // eslint-disable-line
  ampScripts: PropTypes.shape(), // eslint-disable-line
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
};

/**
 * Default props
 *
 * Note: amp-playbuzz script will need to be added only when actual embed is added, there for added from siteapp while data blocks are returned.
 * See https://github.com/ampproject/amphtml/issues/22354
 * https://github.com/autovia-uk/theweekuk-siteapp/pull/117
 */
SocialEmbed.defaultProps = {
  children: [],
  url: '',
  loadStyles: true,
  extraClassNames: {},
  context: {},
  ampScripts: {
    'amp-facebook': 'https://cdn.ampproject.org/v0/amp-facebook-0.1.js',
    'amp-twitter': 'https://cdn.ampproject.org/v0/amp-twitter-0.1.js',
    'amp-instagram': 'https://cdn.ampproject.org/v0/amp-instagram-0.1.js',
  },
};

export default compose(withPolaris, withAmp)(SocialEmbed);
